// React & Nextjs imports
import Link from "next/link";
import Image from "next/image";
import React, { useEffect, useState } from "react";

// Third party imports
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { format } from "date-fns";

// Models & Constants
import { FUNDAMENTAL_TABS, UsStock } from "@/app/interface/global";

// Hooks & Utils & Contexts
import useMobile from "@/app/hooks/use-mobile";
import { trimText } from "@/app/utils/helper";

// Components
import { NewsAndEvents as NewsItem } from "@/app/interface/global";

interface Props {
    stockDetails: UsStock;
    newsItems: NewsItem[] | undefined;
}

const NewsAndEvents: React.FC<Props> = ({ /* stockDetails, */ newsItems }) => {
    const autoPlay = (newsItems && newsItems?.length > 2) || false;
    const autoplayOptions = {
        delay: 3000,
        stopOnInteraction: true,
        playOnInit: true /* autoPlay */,
        stopOnMouseEnter: true,
    };
    const { isMobile } = useMobile();

    const [currIdx, setCurrIdx] = useState(0);

    useEffect(() => {
        console.log("currIdx: ", currIdx);
    });

    const plugins = [];
    plugins.push(Autoplay(autoplayOptions));

    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            align: "start",
            // containScroll: "trimSnaps",
            loop: true,
        },
        plugins,
    );

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => setCurrIdx(emblaApi.selectedScrollSnap());
        emblaApi.on("select", onSelect);
        return () => {
            emblaApi.off("select", onSelect);
        };
    }, [emblaApi]);

    if (newsItems?.length === 0) {
        return <p className="text-sm italic text-gray-500">No news found</p>;
    }

    return (
        <div className="mt-6 w-full max-w-full">
            <h2 className="mb-6 text-3xl font-semibold xs:text-xl">{FUNDAMENTAL_TABS.NEWS_AND_EVENTS}</h2>
            {!newsItems?.length ? (
                <div className="inline-block pt-2 text-sm xs:text-base">No news found</div>
            ) : (
                <div className="flex flex-col lg:flex-row">
                    <div className="flex-1 xs:mt-4 sm:mt-5 lg:mt-0">
                        <div
                            ref={emblaRef}
                            className={
                                /*  */
                                "max-w-[100vw] overflow-hidden xs:max-w-[845px]" +
                                `${(isMobile && newsItems && newsItems?.length > 1) || autoPlay ? " w-full" : ""}`
                            }
                        >
                            <div className="flex pl-1">
                                {newsItems?.map((item: NewsItem, index: number) => {
                                    return (
                                        <Link
                                            key={index}
                                            id={"video-block-" + (index + 1)}
                                            className="relative mx-[6px] ml-2 flex min-h-[88px] min-w-[280px] cursor-pointer flex-row gap-s7 overflow-hidden rounded-xl border border-neutralOpacityDark-10 p-s7 xs:min-h-[201px] xs:flex-col md:max-w-[280px]"
                                            href={item.url}
                                            lang={item.lang}
                                            title={item.headline}
                                            target="_blank"
                                        >
                                            <div
                                                id="video-img"
                                                className="flex min-h-[56px] min-w-[56px] max-w-[56px] items-center rounded-xs xs:min-w-[74px] xs:max-w-full"
                                            >
                                                {item.image ? (
                                                    <Image
                                                        width={56}
                                                        height={56}
                                                        className="h-auto max-h-[56px] w-auto min-w-[56px] max-w-[56px] rounded-xs bg-white opacity-0 transition-all duration-200 xs:max-w-[50%]"
                                                        src={item.image}
                                                        alt={item.source}
                                                        onLoad={(obj) => {
                                                            obj.currentTarget.classList.add(
                                                                "opacity-100",
                                                            );
                                                            if (
                                                                obj.currentTarget.parentElement
                                                                    ?.classList
                                                            ) {
                                                                obj.currentTarget.parentElement.classList.remove(
                                                                    "animate-pulse",
                                                                );
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="flex h-[56px] w-[56px] items-center justify-center rounded-lg bg-gray-100 text-xl text-gray-200">
                                                        x
                                                    </div>
                                                )}
                                            </div>
                                            <p
                                                id="video-title"
                                                className="select-none text-[13px] leading-[150%] xs:text-base xs:leading-[160%]"
                                            >
                                                {trimText(item.headline, isMobile ? 47 : 80)}
                                            </p>
                                            <p className="absolute bottom-[16px] left-[88px] flex flex-wrap items-center text-2xs leading-[140%] tracking-[0%] text-[#9195A9] xs:bottom-4 xs:left-auto xs:block xs:text-xs">
                                                <>
                                                    <span className="mr-1.5 inline-block text-nowrap border-r-0 border-[#c8c8c9] pr-1.5 xs:hidden">
                                                        {item.source}
                                                    </span>

                                                    {format(
                                                        Number(item.datetime) * 1000,
                                                        "yyyy-MM-dd",
                                                    )}
                                                </>
                                            </p>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewsAndEvents;
